import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import {
  DateConditions,
  Filter,
  GridFilterRenderEvents,
  GridFiltersViewData,
  IntEnumConditions,
  StringConditions,
} from '../types';
import {
  API_TYPE_GZ,
  WEB_COMPONENT_BACKEND_ENUM_FILTER,
  WEB_COMPONENT_GZ_COMPANY_FILTER,
  WEB_COMPONENT_GZ_COMPLEX_SEARCH,
  WEB_COMPONENT_GZ_DATETIME_FILTER,
  WEB_COMPONENT_GZ_DROPDOWN,
  WEB_COMPONENT_GZ_ENUM_FILTER,
  WEB_COMPONENT_GZ_INPUT_NUMBER,
  WEB_COMPONENT_GZ_INPUT_SEARCH,
  WEB_COMPONENT_GZ_RANGE_SLIDER_INPUT,
  WEB_COMPONENT_GZ_REMOVABLE,
  WEB_COMPONENT_GZ_SINGLE_SELECT_COMPLEX,
  WEB_COMPONENT_TAGS_NETWORK_FILTER,
  WEB_COMPONENT_GZ_SINGLE_SELECT,
} from '../../../webcomponents.constants';
import { GzEnumFilterWebComponent } from '../../../ui/gzEnumFilter';
import { ColumnDetails, ColumnType } from '../../types/common';
import { GzInputNumberWebComponent, GzInputSearchWebComponent, InputWidthValues } from '../../../ui/gzInput';
import { getColumnKeyForFilter, handleExactMatchCustomValues, isPredefinedFilter } from '../../utils/filters';
import { ElementClass, elementClassListAdd, elementClassListRemove } from '../../../utils/htmlElement.utils';
import { GzDatetimeFilterWebComponent } from '../../../ui/gzDatetimeFilter';
import { AbsoluteTimeRange } from '@nirvana/shared/dtos/types/shared/time';
import {
  getComputedTimeRangeFromToday,
  getTimezonedCurrentDateTimestamp,
  jsTimestampToUnixTimestamp,
} from '@nirvana/shared/time';
import { isEmpty } from '@nirvana/shared/basic-types/object';
import { LabelWidth, LabelWidthValues } from '../../../types/common/types';
import { GzCompanyFilterWebComponent } from '../../../ui/gzCompanyFilter';
import { GzDropdownWebComponent } from '../../../ui/gzDropdown';
import { GzRangeSliderInputWebComponent } from '../../../ui/gzRangeSliderInput';
import { translateFn } from '../../../utils/translator.helper';
import { dataGridStrings } from '../../utils/gridStrings';
import { GzComplexSearchWebComponent } from '../../../ui/gzComplexSearch';
import {
  BackendEnumFilterUiConfig,
  BasicDropdownUiConfig,
  ComplianceSelectDropdownUiConfig,
  DateFilterUiConfig,
  EnumFilterUiConfig,
  GridFilterUiConfiguration,
  NumberFilterUiConfig,
  RemoteFilterUiConfig,
  SingleSelectDropdownUiConfig,
  TextSearchFilterUiConfig,
} from '../../../types/filters/types';
import { COMPLEX_SEARCH_FILTERS } from '../../../edr/incidentsGrid/types';
import { BackendEnumFilterWebComponent } from '../../../filters/backendEnumFilter';
import { RemoteDataCallbackFactory } from '../../../filters/backendEnumFilter/component/RemoteDataCallbackFactory';
import { TagsNetworkFilterWebComponent } from '../../../tagsManager/tagsNetworkFilter';
import { EnumListItem } from '../../../ui/gzEnumList/types';
import { InputEvents } from '../../../ui/gzInput/types';
import { GzSingleSelectComplexWebComponent } from '../../../ui/gzSingleSelectComplex';
import { RangeSliderTranslateStrings } from '../../../ui/gzRangeSliderInput/types';
import { GridFiltersHelper } from '../helpers/GridFiltersHelper';
import { GzSingleSelectWebComponent } from 'apps/webcomponents/src/index.ui';
import { DropdownEvents } from 'apps/webcomponents/src/ui/gzDropdown/types';

interface FilterConfig<Column extends number, ColumnKeys extends string> {
  element: any;
  filter: Filter<ColumnKeys>;
  columnDetails: ColumnDetails<Column, ColumnKeys>;
  filterUiConfig?: any;
  activeFilters?: Array<Filter<ColumnKeys>>;
}

export class GridFiltersView<Column extends number, ColumnKeys extends string> extends BaseWebComponentView {
  private static readonly DATE_FILTER_ACTIVE_INTERVAL = 90;
  public static readonly MORE_FILTERS_ID = 'moreFilters';
  private static readonly MIN_DISPLAYED_FILTERS_COUNT = 2;
  private companyFilterDataProvider;
  private tagsFilterValue: Array<string> = [];
  private companySelectRemoteFilterValue: Filter<ColumnKeys>[ColumnKeys];

  public readonly filterElementsByKey: {
    [key: string]: {
      // The DOM element for the filter.
      filterElement: HTMLElement;

      // The container keeping the filter, allowing for the filter to be removed.
      // Present only for extra filters (these are the only ones that can be removed).
      removeableContainer?: HTMLElement;

      // A function handler which, when called, removes the filter from the DOM.
      removeFn: () => void;
    };
  } = {};

  private static createFilterElement(columnType: ColumnType): HTMLElement {
    switch (columnType) {
      case ColumnType.DATE:
        return document.createElement(WEB_COMPONENT_GZ_DATETIME_FILTER);
      case ColumnType.ENUM:
      case ColumnType.EDITABLE_SELECT:
        return document.createElement(WEB_COMPONENT_GZ_ENUM_FILTER);
      case ColumnType.NUMBER:
        return document.createElement(WEB_COMPONENT_GZ_INPUT_NUMBER);
      case ColumnType.STRING:
        return this.createStringFilterElement();
      case ColumnType.COMPANY:
        return document.createElement(WEB_COMPONENT_GZ_COMPANY_FILTER);
      case ColumnType.SELECT:
        return document.createElement(WEB_COMPONENT_GZ_DROPDOWN);
      case ColumnType.RANGE:
        return document.createElement(WEB_COMPONENT_GZ_RANGE_SLIDER_INPUT);
      case ColumnType.COMPLEX_SEARCH:
        return document.createElement(WEB_COMPONENT_GZ_COMPLEX_SEARCH);
      case ColumnType.COMPLEX_ENUM_FILTER:
        return document.createElement(WEB_COMPONENT_BACKEND_ENUM_FILTER);
      case ColumnType.TAGS:
        return document.createElement(WEB_COMPONENT_TAGS_NETWORK_FILTER);
      case ColumnType.REMOTE_SELECT:
      case ColumnType.BACKEND_SINGLE_SELECT:
        return document.createElement(WEB_COMPONENT_GZ_SINGLE_SELECT_COMPLEX);
      case ColumnType.COMPLIANCE_SELECT:
        return document.createElement(WEB_COMPONENT_GZ_SINGLE_SELECT);
      default:
        throw new Error(`Unknown column type ${columnType}`);
    }
  }

  private static createStringFilterElement(): HTMLElement {
    const stringFilter = document.createElement(WEB_COMPONENT_GZ_INPUT_SEARCH);
    let isClickListenerAdded = false;

    const onClickOutside = (event: MouseEvent) => {
      const path = event.composedPath();
      if (!path.includes(stringFilter)) {
        stringFilter.dispatchEvent(new CustomEvent(InputEvents.BLUR, { bubbles: true, composed: true }));
      }
      // Remove the click event listener after it's triggered
      document.removeEventListener('click', onClickOutside);
      isClickListenerAdded = false;
    };

    stringFilter.addEventListener(InputEvents.BLUR, e => {
      // Check if the event is already a custom event to prevent infinite loop
      if (!e.isTrusted) {
        return;
      }
      if (!isClickListenerAdded) {
        document.addEventListener('click', onClickOutside);
        isClickListenerAdded = true;
      }
    });

    return stringFilter;
  }

  getTranslations() {
    this.commonStrings = this.stringsTranslator.getCommonStrings();
  }

  getDomElements() {
    this.dom.$wrapper = this.shadow.querySelector('.wrapper');
    this.dom.$filtersContainer = this.shadow.querySelector('.filters-container');
    this.dom.$filtersSeparator = this.shadow.getElementById('filters-separator');
    this.dom.$moreFilters = this.shadow.getElementById(GridFiltersView.MORE_FILTERS_ID);
    this.dom.$clearFilters = this.shadow.querySelector('#clearFilters');
    this.dom.$clearFiltersWrapper = this.shadow.querySelector('#clearFiltersWrapper');

    this.getTranslations();
    this.setMoreFiltersProps();
  }

  buildSuccessView(data: GridFiltersViewData<Column, ColumnKeys>) {
    if (data.companyFilterDataProvider) {
      // overwrite data provider
      this.companyFilterDataProvider = data.companyFilterDataProvider;
    }

    if (!data) {
      this.dom.$wrapper.innerHTML = '';
      return;
    }

    this.updateFilters(data);
    this.updateMoreFilters(data);
    this.renderResetFiltersButton(data.filters.length, data.hideResetFilters);

    this.dom.$wrapper.dispatchEvent(
      new CustomEvent(GridFilterRenderEvents.FILTERS_RENDER_DONE, { bubbles: true, composed: true })
    );
  }

  private renderResetFiltersButton(filtersCount: number, hideResetFilters = false) {
    if (
      hideResetFilters &&
      filtersCount <= GridFiltersView.MIN_DISPLAYED_FILTERS_COUNT &&
      !this.dom.$moreFilters.filterOptions?.length
    ) {
      elementClassListAdd(this.dom.$clearFiltersWrapper, 'd-none');
    } else {
      elementClassListRemove(this.dom.$clearFiltersWrapper, 'd-none');
    }
  }

  public updateFilters(data: GridFiltersViewData<Column, ColumnKeys>) {
    const existingFiltersKeys: { [key: string]: boolean } = {};
    const { filtersUiConfigs } = data;
    for (const filter of data.filters || []) {
      const columnKey = getColumnKeyForFilter(filter);
      const columnDetails = data.columnDetailsByKey[columnKey];

      if (!filter.hasOwnProperty(columnDetails.key)) {
        throw new Error(`Unexpected filter with key ${columnDetails.key} - missing filter value`);
      }

      existingFiltersKeys[columnDetails.key] = true;

      const columnType = columnDetails.type;

      if (!this.filterElementsByKey[columnDetails.key]) {
        const filterElement = GridFiltersView.createFilterElement(columnType);
        filterElement.setAttribute('columnKey', columnDetails.key);

        if (isPredefinedFilter(columnDetails.key, data.predefinedFiltersKeys)) {
          elementClassListAdd(filterElement, ElementClass.FILTER);
          this.dom.$filtersContainer.insertBefore(filterElement, this.dom.$filtersSeparator);
          this.filterElementsByKey[columnDetails.key] = {
            filterElement,
            removeFn: () => {
              this.dom.$filtersContainer.removeChild(filterElement);
            },
          };
        } else {
          const removableWrapper = document.createElement(WEB_COMPONENT_GZ_REMOVABLE);
          removableWrapper.setAttribute('columnKey', columnDetails.key);
          elementClassListAdd(removableWrapper, ElementClass.FILTER);
          removableWrapper.appendChild(filterElement);
          this.dom.$filtersContainer.insertBefore(removableWrapper, this.dom.$moreFilters);
          this.filterElementsByKey[columnDetails.key] = {
            filterElement: filterElement,
            removeableContainer: removableWrapper,
            removeFn: () => {
              this.dom.$filtersContainer.removeChild(removableWrapper);
            },
          };
        }
      }

      const element = this.filterElementsByKey[columnDetails.key].filterElement;

      let uiConfig;
      if (filtersUiConfigs && !isEmpty(filtersUiConfigs)) {
        uiConfig = this.getFiltersUiConfigByKey(filtersUiConfigs, columnKey);
      }

      const filterConfig: FilterConfig<Column, ColumnKeys> = {
        element,
        filter,
        columnDetails,
        filterUiConfig: uiConfig,
        activeFilters: data.filters,
      };
      this.updateFiltersProps(filterConfig);
    }

    this.removeUnusedFilters(existingFiltersKeys);
  }

  private getFiltersUiConfigByKey(filtersUiConfigs: GridFilterUiConfiguration[], columnKey: ColumnKeys) {
    const matched = filtersUiConfigs.find(config => config.key === columnKey);
    return matched ? matched.config : null;
  }

  /**
   * Removes all DOM elements for filters that are not used anymore.
   */
  private removeUnusedFilters(existingFiltersKeys: { [key: string]: boolean }) {
    const filterElementsKeys = Object.keys(this.filterElementsByKey);
    for (const key of filterElementsKeys) {
      if (!existingFiltersKeys[key]) {
        const elem = this.filterElementsByKey[key];
        elem.removeFn();
        delete this.filterElementsByKey[key];
      }
    }
  }

  private setMoreFiltersProps() {
    this.dom.$moreFilters.labelWidth = LabelWidthValues.AUTO;
    this.dom.$moreFilters.label = this.commonStrings.more;
    this.dom.$moreFilters.noSearchResultsString = dataGridStrings().noOptionsFound;
    this.dom.$moreFilters.confirmActionButtonString = this.commonStrings.apply;
    this.dom.$moreFilters.searchPlaceholderString = this.commonStrings.search;
    this.dom.$moreFilters.allItemsLabel = this.commonStrings.all;
    this.dom.$clearFilters.innerHTML = dataGridStrings().resetFiltersString;
  }

  private updateMoreFilters(data: GridFiltersViewData<Column, ColumnKeys>) {
    // Add all filters except the predefined ones, that should always be present.
    const columnKeyToColumn = {};

    const filterOptionsByColumn = {};

    for (const columnDetails of Object.values(data.columnDetailsByKey)) {
      const { key, column, columnName, type, filteringDisabled } = columnDetails;

      columnKeyToColumn[key as string] = columnDetails.column;

      // Don't show an actions column or a non-filterable column in the `More` filter
      if (filteringDisabled || type === ColumnType.ACTION || type === ColumnType.CUSTOM) {
        continue;
      }

      if (!isPredefinedFilter(columnDetails.key, data.predefinedFiltersKeys)) {
        filterOptionsByColumn[column as number] = {
          label: columnName,
          value: column,
          selected: false,
        };
      }
    }

    // Set filters that are already visible as selected.
    for (const filter of data.filters || []) {
      const columnKey = getColumnKeyForFilter(filter);

      if (!isPredefinedFilter(columnKey, data.predefinedFiltersKeys)) {
        const column = columnKeyToColumn[columnKey as string];
        if (!filterOptionsByColumn[column]) {
          throw new Error(`No column found for filter with key ${columnKey}`);
        }
        filterOptionsByColumn[column].selected = true;
      }
    }

    // There are no extra filters to display in the 'More' dropdown, so we need to hide it
    if (isEmpty(filterOptionsByColumn)) {
      elementClassListAdd(this.dom.$moreFilters, 'd-none');
      return;
    }

    elementClassListRemove(this.dom.$moreFilters, 'd-none');

    this.dom.$moreFilters.filterOptions = Object.values(filterOptionsByColumn);
    this.dom.$moreFilters.canSelectAll = this.dom.$moreFilters.filterOptions?.length > 1;
  }

  private updateFiltersProps(filterConfigs: FilterConfig<Column, ColumnKeys>) {
    const { element, filter, columnDetails, filterUiConfig } = filterConfigs;
    switch (columnDetails.type) {
      case ColumnType.DATE:
        const dateFilterComponent = element as GzDatetimeFilterWebComponent;
        this.updateDateFilterProps({ element: dateFilterComponent, filter, columnDetails, filterUiConfig });
        break;
      case ColumnType.ENUM:
      case ColumnType.EDITABLE_SELECT:
        this.updateEnumFilterProps(
          element as GzEnumFilterWebComponent,
          filter,
          columnDetails,
          filterUiConfig as EnumFilterUiConfig
        );
        break;
      case ColumnType.NUMBER:
        this.updateNumberFilterProps(
          element as GzInputNumberWebComponent,
          filter,
          columnDetails,
          filterUiConfig as NumberFilterUiConfig
        );
        break;
      case ColumnType.STRING:
        this.updateStringFilterProps(element as GzInputSearchWebComponent, filter, columnDetails, filterUiConfig);
        break;
      case ColumnType.COMPANY:
        this.updateCompanyFilterProps(element as GzCompanyFilterWebComponent, filter, columnDetails, filterUiConfig);
        break;
      case ColumnType.SELECT:
        this.updateSelectFilterProps(element, filter, columnDetails, filterUiConfig);
        break;
      case ColumnType.RANGE:
        this.updateRangeFilterProps(element, filter, columnDetails, filterUiConfig);
        break;
      case ColumnType.COMPLEX_SEARCH:
        this.updateComplexSearchFilterProps(element, filter, columnDetails);
        break;
      case ColumnType.COMPLEX_ENUM_FILTER:
        this.updateBackendEnumFilterProps(element, filter, columnDetails, filterUiConfig);
        break;
      case ColumnType.TAGS:
        this.updateTagsFilterProps(
          element as TagsNetworkFilterWebComponent,
          filter,
          columnDetails,
          filterConfigs.activeFilters!
        );
        break;
      case ColumnType.REMOTE_SELECT:
      case ColumnType.BACKEND_SINGLE_SELECT:
        this.updateRemoteSelectFilterProps(element, filter, columnDetails, filterUiConfig);
        break;
      case ColumnType.COMPLIANCE_SELECT:
        this.updateComplianceSelectFilterProps(element, filter, columnDetails, filterUiConfig);
        break;
      default:
        throw new Error(`Unknown column type ${columnDetails.type}`);
    }
  }

  private updateNumberFilterProps(
    numberFilter: GzInputNumberWebComponent,
    filter: Filter<ColumnKeys>,
    columnDetails: ColumnDetails<Column, ColumnKeys>,
    filterUiConfig: NumberFilterUiConfig = {}
  ) {
    const { key, columnName } = columnDetails;
    const filterValue = filter[key];

    numberFilter.inputWidth = filterUiConfig?.inputWidth ?? InputWidthValues.MEDIUM;
    numberFilter.label = columnName;
    numberFilter.placeholder = '00';
    numberFilter.unitPrefix = '#';
    numberFilter.searchOnAction = true;

    if (filterValue.hasOwnProperty('eq')) {
      numberFilter.value = filterValue.eq;
      numberFilter.forceUpdateClearButtonVisibility();
    }

    if (filterUiConfig?.min !== undefined) {
      numberFilter.min = filterUiConfig.min;
    }
    if (filterUiConfig?.max !== undefined) {
      numberFilter.max = filterUiConfig.max;
    }
  }

  private updateEnumFilterProps(
    enumFilterComponent: GzEnumFilterWebComponent,
    filter: Filter<ColumnKeys>,
    columnDetails: ColumnDetails<Column, ColumnKeys>,
    filterUiConfig: EnumFilterUiConfig = {}
  ) {
    enumFilterComponent.label = columnDetails.columnName;
    enumFilterComponent.labelWidth = filterUiConfig?.labelWidth ?? LabelWidthValues.LARGE;
    enumFilterComponent.confirmActionButtonString = this.commonStrings.apply;

    if (columnDetails?.maxSelections) {
      enumFilterComponent.maxSelections = columnDetails?.maxSelections;
    }

    if (columnDetails?.exceededLimitError) {
      enumFilterComponent.exceededLimitError = columnDetails?.exceededLimitError;
    }

    enumFilterComponent.searchPlaceholderString = this.commonStrings.search;
    enumFilterComponent.canSelectAll = columnDetails?.enumFilterCanSelectAll ?? filterUiConfig?.canSelectAll ?? true;
    enumFilterComponent.allItemsLabel = this.commonStrings.all;
    enumFilterComponent.noSearchResultsString = dataGridStrings().noOptionsFound;
    enumFilterComponent.isClearable = filterUiConfig?.isClearable ?? enumFilterComponent.isClearable;
    enumFilterComponent.allowSelectNone = filterUiConfig?.allowSelectNone ?? enumFilterComponent.allowSelectNone;

    const enumValues = columnDetails.values || [];
    if (enumValues.length === 0) {
      throw new Error(`Unexpected enum filter: empty values for column with key ${columnDetails.key}`);
    }

    const filterValue = filter[columnDetails.key] as IntEnumConditions;
    const selectedValues = {};
    for (const value of filterValue.in) {
      selectedValues[value] = true;
    }

    enumFilterComponent.filterOptions = enumValues.map(enumValue => ({
      label: enumValue.label,
      value: enumValue.value,
      parent: enumValue.parent,
      selected: selectedValues[enumValue.value] ? true : false,
    }));
  }

  private updateStringFilterProps(
    stringFilter: GzInputSearchWebComponent,
    filter: Filter<ColumnKeys>,
    columnDetails: ColumnDetails<Column, ColumnKeys>,
    filterUiConfig: TextSearchFilterUiConfig = {}
  ) {
    const { key, columnName } = columnDetails;
    const filterValue = filter[key] as StringConditions;
    const numConditions = Object.keys(filterValue).length;

    stringFilter.inputWidth = filterUiConfig?.inputWidth ?? InputWidthValues.LARGE;
    stringFilter.label = columnName;
    stringFilter.placeholder = filterUiConfig?.placeholder ?? dataGridStrings().stringFilterPlaceholder;
    stringFilter.searchOnAction = true;

    if (numConditions !== 1) {
      throw new Error(`Currently, filter not supported for StringConditions: ${JSON.stringify(filterValue, null, 2)}.`);
    }

    if (filterValue.hasOwnProperty('startsWith') && filterValue.startsWith) {
      stringFilter.value = filterValue.startsWith;
    } else if (filterValue.hasOwnProperty('regex') && filterValue.regex) {
      stringFilter.value = filterValue.regex.pattern;
    } else if (filterValue.hasOwnProperty('equals') && filterValue.equals) {
      stringFilter.value = filterValue.equals;
    } else if (filterValue.hasOwnProperty('contains') && filterValue.contains === '') {
      // Filter matching all records.
      stringFilter.value = filterValue.contains;
    } else {
      throw new Error(`Currently, filter not supported for StringConditions: ${JSON.stringify(filterValue, null, 2)}.`);
    }
  }

  private updateDateFilterProps(filterConfigs: FilterConfig<Column, ColumnKeys>) {
    const {
      filter,
      columnDetails,
      filterUiConfig: {
        activeRange,
        labelWidth,
        enableSelectAllToDate = false,
        additionalTimeRangeOptions = [],
        max,
      } = {} as DateFilterUiConfig,
    } = filterConfigs;

    const dateFilterComponent = filterConfigs.element as GzDatetimeFilterWebComponent;
    const timezone = this.dependencies.getTimezone();
    const { key } = columnDetails;
    const filterValue = filter[key] as DateConditions;

    dateFilterComponent.label = columnDetails.columnName;
    dateFilterComponent.labelWidth = labelWidth ?? LabelWidthValues.LARGE;
    dateFilterComponent.enableSelectAllToDate = enableSelectAllToDate;
    dateFilterComponent.showTimePickers = columnDetails.showTimePickers ?? true;
    dateFilterComponent.userLocale = this.dependencies.getUserLanguage() || 'en_US';
    dateFilterComponent.timezone = timezone;
    dateFilterComponent.timeRangeOptions = [
      {
        label: this.commonStrings.timeRangeOptions.last24h,
        value: 1,
      },
      {
        label: this.commonStrings.timeRangeOptions.last7days,
        value: 7,
      },
      {
        label: this.commonStrings.timeRangeOptions.last30days,
        value: 30,
      },
      ...additionalTimeRangeOptions,
    ];

    dateFilterComponent.translateStrings = {
      confirmButton: this.commonStrings.apply,
      cancelButton: this.commonStrings.cancel,
      fromLabel: this.commonStrings.from,
      toLabel: this.commonStrings.to,
      customLabel: this.dependencies.translateFn('Custom'),
      allToDate: this.dependencies.translateFn('All (to date)'),
    };

    if (filterValue.timeRange && !isEmpty(filterValue.timeRange)) {
      dateFilterComponent.setSelectedTimeRange(filterValue.timeRange);
    }

    const interval: number | null =
      activeRange !== undefined
        ? activeRange
        : additionalTimeRangeOptions.length
        ? Math.max(...dateFilterComponent.timeRangeOptions.map(entry => entry.value))
        : GridFiltersView.DATE_FILTER_ACTIVE_INTERVAL;

    if (interval !== null) {
      const activeDateFilterRange: AbsoluteTimeRange = getComputedTimeRangeFromToday(
        interval, // this can be customized
        timezone
      );
      const { fromTimestamp, toTimestamp } = activeDateFilterRange;

      if (fromTimestamp && toTimestamp) {
        dateFilterComponent.min = jsTimestampToUnixTimestamp(fromTimestamp);
      }
    }

    // the max date limit is set to today, unless it is explicitly set to null in the filter UI config. In that case, the max date limit is removed from the date picker
    dateFilterComponent.max = max === null ? undefined : getTimezonedCurrentDateTimestamp(timezone);

    // clear selected filter value
    if (isEmpty(filterValue.timeRange)) {
      dateFilterComponent.setSelectedTimeRange(null);
    }
  }

  private async updateTagsFilterProps(
    tagsFilterComponent: TagsNetworkFilterWebComponent,
    filter: Filter<ColumnKeys>,
    columnDetails: ColumnDetails<Column, ColumnKeys>,
    activeFilters: Array<Filter<ColumnKeys>>
  ) {
    this.tagsFilterValue = [];
    const companyFilterComponent = this.dom.$companyFilter as
      | GzEnumFilterWebComponent
      | GzSingleSelectComplexWebComponent;
    const isSingleSelectCompanyFilter = companyFilterComponent instanceof GzSingleSelectComplexWebComponent;
    const isCompanyFilterPresent = !!companyFilterComponent;
    const companyData = Application.Main.getUserCompany();
    const isCompanyFilterBeingRemoved =
      isCompanyFilterPresent && activeFilters.filter(filter => filter.hasOwnProperty('company')).length === 0;

    let companySelectedOptions = [
      {
        label: companyData.name,
        value: companyData.id,
        selected: true,
      },
    ];

    if (isCompanyFilterPresent) {
      if (isSingleSelectCompanyFilter) {
        const selectedCompanyValue = companyFilterComponent.value;
        const selectedCompanyLabel = companyFilterComponent.filterOptions.find(
          option => option.value === selectedCompanyValue
        )?.label;

        if (selectedCompanyLabel && selectedCompanyValue) {
          companySelectedOptions = [
            {
              label: selectedCompanyLabel,
              value: selectedCompanyValue,
              selected: true,
            },
          ];
        } else {
          companySelectedOptions = [];
        }
      } else {
        companySelectedOptions = companyFilterComponent.filterOptions.filter(option => option.selected);
      }
    }

    this.updateTagsFilterWithSelectedCompanies(
      companySelectedOptions,
      isCompanyFilterPresent,
      tagsFilterComponent,
      isCompanyFilterBeingRemoved
    );

    tagsFilterComponent.shouldUsePluralOnEmptyStateText = isCompanyFilterPresent ? !isSingleSelectCompanyFilter : false;
    tagsFilterComponent.label = columnDetails.columnName;
    tagsFilterComponent.labelWidth = LabelWidthValues.LARGE;
    tagsFilterComponent.injectDependencies({
      ...this.dependencies,
      api: {
        ...this.dependencies.api,
        type: API_TYPE_GZ,
      },
      permissions: {
        canHaveChildCompanies: Application.Main.canHaveChildCompanies(),
      },
      isCloud: Application.Main.product.isCloud,
    });

    const filterValue = filter[columnDetails.key].in || [];

    this.tagsFilterValue = filterValue;
    tagsFilterComponent
      .start()
      .then(() => {
        tagsFilterComponent.setSelectedItems(filterValue);
        tagsFilterComponent.controller.view.dom.$tagFilterSelector.isClearable = true;
      })
      .catch(error => console.error(error));

    this.dom.$tagsFilter = tagsFilterComponent;
  }

  public async updateTagsFilterWithRemoteSelectorOption(option: { label: string; selectedValue: string }) {
    const tagsFilterComponent = this.dom.$tagsFilter as TagsNetworkFilterWebComponent;

    if (!tagsFilterComponent) {
      return;
    }

    tagsFilterComponent.companyIds = [option.selectedValue];
    tagsFilterComponent.companyNames = [option.label];

    if (this.tagsFilterValue.length > 0) {
      tagsFilterComponent.setSelectedItems(this.tagsFilterValue);
      this.tagsFilterValue = [];
    }
  }

  public updateTagsFilterWithSelectedCompanies(
    selectedOptions: Array<EnumListItem>,
    isCompanyFilterPresent: boolean = true,
    tagsFilterComponent?: TagsNetworkFilterWebComponent,
    isCompanyFilterBeingRemoved?: boolean
  ) {
    const companyFilterComponent = this.dom.$companyFilter as GzCompanyFilterWebComponent;

    if (!tagsFilterComponent) {
      tagsFilterComponent = this.dom.$tagsFilter as TagsNetworkFilterWebComponent;
    }

    if (!tagsFilterComponent) {
      return;
    }

    if (isCompanyFilterBeingRemoved) {
      this.displayInfoTemplateForTagsFilter(tagsFilterComponent);
      return;
    }

    if (isCompanyFilterPresent && companyFilterComponent?.specialValueSelected) {
      // we will not display any tags in these cases
      this.displayInfoTemplateForTagsFilter(tagsFilterComponent);
      return;
    }

    const companyNames = selectedOptions.map(selectedOption => selectedOption.label);
    const companyIds = selectedOptions.map(selectedOption => selectedOption.value as string);

    tagsFilterComponent.shouldFetchData = companyIds.length > 0;
    tagsFilterComponent.companyIds = companyIds;
    tagsFilterComponent.companyNames = companyNames;

    if (companyFilterComponent && this.tagsFilterValue.length > 0) {
      // if we have company filter (for Partners) we should also set here selected items,
      // this code is also called after the company filter is set; these will cause the tags filter to re-render with correct values
      tagsFilterComponent.setSelectedItems(this.tagsFilterValue);
      this.tagsFilterValue = [];
    }
  }

  private updateCompanyFilterProps(
    companyFilterComponent: GzCompanyFilterWebComponent,
    filter: Filter<ColumnKeys>,
    columnDetails: ColumnDetails<Column, ColumnKeys>,
    filterUiConfig: EnumFilterUiConfig
  ) {
    companyFilterComponent.injectDependencies({
      ...this.dependencies,
      api: {
        ...this.dependencies.api,
        type: this.companyFilterDataProvider,
      },
    });
    companyFilterComponent.label = columnDetails.columnName;
    companyFilterComponent.labelWidth = filterUiConfig?.labelWidth ?? LabelWidthValues.LARGE;
    companyFilterComponent.placeholder = translateFn('Select company');
    companyFilterComponent.noSearchResultsString = dataGridStrings().noOptionsFound;
    companyFilterComponent.confirmActionButtonString = this.commonStrings.apply;
    companyFilterComponent.searchPlaceholderString = this.commonStrings.search;
    companyFilterComponent.canSelectAll =
      columnDetails?.enumFilterCanSelectAll === undefined ? true : columnDetails?.enumFilterCanSelectAll;
    companyFilterComponent.showOnlySelectAll = columnDetails?.showOnlySelectAll || false;
    companyFilterComponent.showNone = columnDetails?.showNone || false;
    companyFilterComponent.allItemsLabel = dataGridStrings().allRecursive;
    companyFilterComponent.allDirectManagementLabel = dataGridStrings().allDirectManagement;
    companyFilterComponent.errorMessage = this.commonStrings.genericError;
    companyFilterComponent.overwriteList = true;
    companyFilterComponent.allowSelectNone = filterUiConfig?.allowSelectNone ?? false;
    companyFilterComponent.isClearable = filterUiConfig?.isClearable ?? false;

    const filterValue = filter[columnDetails.key].in || [];
    const selectedValues = {};
    for (const value of filterValue) {
      selectedValues[value] = true;
    }

    companyFilterComponent.controller.setFilterOptions(selectedValues);

    this.dom.$companyFilter = companyFilterComponent;
  }

  private updateSelectFilterProps(
    select: GzDropdownWebComponent,
    filter: Filter<ColumnKeys>,
    columnDetails: ColumnDetails<Column, ColumnKeys>,
    filterUiConfig: SingleSelectDropdownUiConfig
  ) {
    const { key } = columnDetails;
    const { value } = filter[key];
    const shouldClearSelectValue = !value && value !== 0 && select.value;

    select.label = columnDetails.columnName;
    select.labelWidth = (filterUiConfig?.labelWidth as LabelWidth) ?? LabelWidthValues.LARGE;
    select.classList.add('w-auto');
    select.canDelete = filterUiConfig?.canDelete ?? true;
    select.openOnClear = false;

    select.options = columnDetails.values!.map(({ label: title, value }) => ({ value, title }));

    if (shouldClearSelectValue) {
      select.clearInputValue();
    }

    // This is a bugfix avoiding grid re-renders when we set this value automatically from a smart view
    if (columnDetails.isFilter) {
      select.value = value;
    } else {
      select.setFormValue(value);
    }

    // This is a bugfix for when changing smart views, see https://bitdefender.atlassian.net/browse/CERES-53103
    if (!!value) {
      select.setShowDeleteIcon(true);
      select.updateComplete.then(() => select.requestUpdate());
    }
  }

  private async updateComplianceSelectFilterProps(
    select: GzSingleSelectWebComponent,
    filter: Filter<ColumnKeys>,
    columnDetails: ColumnDetails<Column, ColumnKeys>,
    filterUiConfig: ComplianceSelectDropdownUiConfig
  ) {
    const { key } = columnDetails;
    const { value } = filter[key];

    select.label = columnDetails.columnName;
    select.isFilter = true;
    select.labelWidth = (filterUiConfig?.labelWidth as LabelWidth) ?? LabelWidthValues.LARGE;
    select.classList.add('w-auto');
    select.searchPlaceholderString = translateFn('Search');
    select.noSearchResultsString = translateFn('No options found');
    select.cannotDelete = !filterUiConfig?.canDelete || true;
    const compliancePremiumUsable = await filterUiConfig.hasCompliancePremiumUsable();;
    select.filterOptions = columnDetails.values!.map(({ label, value, parent }) => {
    const itemSuffixRenderer =filterUiConfig.itemSuffixRenderer;
      return { 
        value, 
        label, 
        parent,
        itemSuffixRenderer: (value) => itemSuffixRenderer(value, compliancePremiumUsable),
      }
    }); 
    select.value = value;
  }

  private updateRangeFilterProps(
    rangeFilterComponent: GzRangeSliderInputWebComponent,
    filter: Filter<ColumnKeys>,
    columnDetails: ColumnDetails<Column, ColumnKeys>,
    filterUiConfig: BasicDropdownUiConfig
  ) {
    const { key } = columnDetails;

    rangeFilterComponent.placeholder = `${translateFn('Choose')}...`;
    rangeFilterComponent.translateStrings = <RangeSliderTranslateStrings>{
      fromLabel: translateFn('From'),
      toLabel: translateFn('To'),
      confirmButton: translateFn('Confirm'),
      cancelButton: translateFn('Cancel'),
    };
    rangeFilterComponent.label = columnDetails.columnName;
    rangeFilterComponent.labelWidth = filterUiConfig?.labelWidth ?? LabelWidthValues.LARGE;
    rangeFilterComponent.autoAdjustMenuPosition = true;

    rangeFilterComponent.setSliderRange(filter[key]['range']);
  }

  private updateComplexSearchFilterProps(
    complexSearchFilterComponent: GzComplexSearchWebComponent,
    filter: Filter<ColumnKeys>,
    columnDetails: ColumnDetails<Column, ColumnKeys>
  ) {
    const { key } = columnDetails;

    const { field, value } = filter[key]['complexSearch'];

    complexSearchFilterComponent.label = translateFn('Parameter');
    complexSearchFilterComponent.dropdownPlaceholder = translateFn('Select');
    complexSearchFilterComponent.inputPlaceholder = translateFn('Type here (min. 3 characters)');
    complexSearchFilterComponent.searchPlaceholderString = translateFn('Search');
    complexSearchFilterComponent.customFilters = {
      [COMPLEX_SEARCH_FILTERS.PROCESS_PID]: translateFn('Type the PID'),
    };

    complexSearchFilterComponent.searchKey = field;
    complexSearchFilterComponent.searchKeyOptions = columnDetails.values!;
    complexSearchFilterComponent.searchValue = value;
  }

  private updateBackendEnumFilterProps(
    backendEnumFilterComponent: BackendEnumFilterWebComponent,
    filter: Filter<ColumnKeys>,
    columnDetails: ColumnDetails<Column, ColumnKeys>,
    filterUiConfig: BackendEnumFilterUiConfig
  ) {
    backendEnumFilterComponent.label = filterUiConfig?.filterLabel ?? columnDetails.columnName;
    backendEnumFilterComponent.searchPlaceholderString = translateFn('Type here');
    backendEnumFilterComponent.placeholder = translateFn('Select an option');
    backendEnumFilterComponent.confirmActionButtonString = translateFn('Apply');
    backendEnumFilterComponent.clearSelectionButtonString = translateFn('Clear selection');
    backendEnumFilterComponent.noSearchResultsString = translateFn('No suggestions available');
    backendEnumFilterComponent.errorMessageDetails = {
      message: translateFn(
        'Could not load suggestions. Contact {OPEN_TAG} Bitdefender Enterprise Support {CLOSE_TAG}',
        {
          OPEN_TAG: '<gz-link class="d-inline" href="https://www.bitdefender.com/business/support/en/71263-85158-contact.html">',
          CLOSE_TAG: '</gz-link>',
        }
      ),
    };

    backendEnumFilterComponent.allItemsLabel = translateFn('All');
    backendEnumFilterComponent.ignoredFieldsForFilters = columnDetails.remoteDetails?.ignoredFieldsForFilters ?? [];
    backendEnumFilterComponent.allowSelectNone = false;
    backendEnumFilterComponent.labelWidth = filterUiConfig?.labelWidth ?? LabelWidthValues.MEDIUM;

    if (filterUiConfig?.iconSet) {
      backendEnumFilterComponent.iconSet = filterUiConfig.iconSet;
    }

    const remoteDetailsType = columnDetails.remoteDetails!.type;

    backendEnumFilterComponent.getRemoteDataCallback =
      RemoteDataCallbackFactory.getRemoteDataCallback(remoteDetailsType);
    backendEnumFilterComponent.convertResponseDataCallback =
      RemoteDataCallbackFactory.getConvertResponseCallback(remoteDetailsType);

    backendEnumFilterComponent.getTypeToIconMappingCallback =
      RemoteDataCallbackFactory.getTypeToIconMappingCallback(remoteDetailsType);

    backendEnumFilterComponent.getExtraFiltersCallback =
      RemoteDataCallbackFactory.getExtraFiltersCallback(remoteDetailsType);

    backendEnumFilterComponent.getLabelsCallback = RemoteDataCallbackFactory.getLabelsCallback(remoteDetailsType);

    const { key } = columnDetails;

    const initialPinnedItems = filter[key]?.['complexEnumFilter'] ?? [];

    backendEnumFilterComponent.initialPinnedItems = initialPinnedItems.map(item => {
      return {
        ...item,
        checked: true,
      };
    });
  }

  private displayInfoTemplateForTagsFilter(tagsFilterComponent: TagsNetworkFilterWebComponent) {
    tagsFilterComponent.companyIds = [];
    tagsFilterComponent.companyNames = [];
    tagsFilterComponent.shouldFetchData = false;
  }

  updateRemoteSelectFilterProps(
    filterComponent: GzSingleSelectComplexWebComponent,
    filter: Filter<ColumnKeys>,
    columnDetails: ColumnDetails<Column, ColumnKeys>,
    filterUiConfig: RemoteFilterUiConfig
  ) {
    const key = columnDetails.key;
    const value = filter[key];
    if (key === 'companyId') {
      this.companySelectRemoteFilterValue = value;
    }
    if (key === 'company') {
      this.dom.$companyFilter = filterComponent;
    }

    handleExactMatchCustomValues(value);
    filterComponent.isFilter = true;
    filterComponent.cannotDelete = !filterUiConfig.canDelete;
    filterComponent.apiType = filterUiConfig.apiType;
    filterComponent.filterType = GridFiltersHelper.getSingleSelectFilterTypeByColumnKey(key);
    filterComponent.label = columnDetails.columnName;
    filterComponent.labelWidth = filterUiConfig?.labelWidth ?? LabelWidthValues.LARGE;
    filterComponent.noSearchResultsString = dataGridStrings().noOptionsFound;
    filterComponent.searchPlaceholderString = this.commonStrings.search;
    filterComponent.errorTooltipText = this.commonStrings.genericError;
    filterComponent.placeholder = filterUiConfig?.placeholder ?? '';

    if (filterUiConfig.populateFilterCallback) {
      filterUiConfig.populateFilterCallback(filterComponent, value);
    }
  }
}
